import { Box, Card, Inline, L88, Stack } from "@adfenix/alexandria";
import { ProgramService, TemplateService } from "@adfenix/types";
import AdjacentPopup from "components/AdjacentPopup";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { TemplatePreview } from "./AdTemplatePreview";
import { ImageTemplatePreview } from "./ImageTemplatePreview";
import { ReadLayerWrapper } from "components/ReadLayer/ReadLayerWrapper";
import { SearchField } from "components/ReadLayer/SearchField";
import { t } from "i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { HqAndOfficeIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import { SubjectTable } from "./SubjectTable";
import { IconButton } from "components/IconButton/IconButton";
import { SolidArrowLeft, SolidX } from "@adfenix/icons";
import { subjectToCamelCase } from "utils/types";

const StyledCard = styled(Card)`
  max-width: 500px;
  background-color: ${({ theme }) => L88(theme.colors.grey)};
`;

interface Props {
  parentSelector: string;
  templateId: string | null;
  channel?: TemplateService.ChannelType;
  onClose: () => void;
  type: "ad" | "image";
  subjectType: ProgramService.SubjectType;
}

export interface SelectedSubject {
  id: string;
  image: string | undefined;
}

export const PreviewTemplateModal = ({
  parentSelector,
  templateId,
  channel,
  type,
  subjectType,
  onClose
}: Props) => {
  const [selectedSubject, setSelectedSubject] =
    useState<SelectedSubject | null>(null);
  const { hqId } = useParams<HqAndOfficeIdRouteParams>();
  const [_, setSearchParams] = useSearchParams();
  useEffect(() => {
    setSelectedSubject(null);
    // Reset the search params which is modified by the read-layer context when changing template to avoid incorrect searches.
    // Including setSearchParams in the effect will cause the searchParams to be reset as soon as they are set.
    setSearchParams("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  if (!templateId || (!channel && type === "ad") || !hqId) {
    return null;
  }

  const {
    filters,
    indexName,
    serviceName,
    sortAscending,
    sortOnColumn,
    columns
  } = getReadLayerSettings(subjectType, hqId);

  return (
    <AdjacentPopup
      parentSelector={parentSelector}
      targetSelector={`#preview-button-${templateId}`}
    >
      <StyledCard p="m">
        <Inline gap="m" justifyContent="end">
          {selectedSubject && (
            <IconButton label="back" onClick={() => setSelectedSubject(null)}>
              <SolidArrowLeft />
            </IconButton>
          )}
          <IconButton onClick={onClose} label="close-template-preview">
            <SolidX />
          </IconButton>
        </Inline>
        {!selectedSubject && (
          <Box display="flex" flexDirection="row">
            <ReadLayerWrapper
              serviceName={serviceName}
              indexName={indexName}
              sortOnColumn={sortOnColumn}
              sortAscending={sortAscending}
              filters={filters}
            >
              <Stack spacing="l" pY="m">
                <SearchField
                  placeholder={t(
                    "programBuilder.program.details.testOrderPage.search.placeholder"
                  )}
                  columns={columns}
                />
                <SubjectTable
                  templateType={type}
                  subjectType={subjectType}
                  setItem={setSelectedSubject}
                />
              </Stack>
            </ReadLayerWrapper>
          </Box>
        )}

        {type === "image" ? (
          <ImageTemplatePreview
            mainImageUrl={selectedSubject?.image}
            subjectId={selectedSubject?.id}
            templateId={templateId}
            subjectType={subjectToCamelCase(subjectType)}
          />
        ) : (
          <TemplatePreview
            subjectId={selectedSubject?.id}
            subjectType={subjectToCamelCase(subjectType)}
            channel={channel!}
            templateId={templateId}
          />
        )}
      </StyledCard>
    </AdjacentPopup>
  );
};

const getReadLayerSettings = (
  subjectType: ProgramService.SubjectType,
  hqId: string
) => {
  switch (subjectType) {
    case "Property":
      return {
        serviceName: "property",
        indexName: "property",
        sortOnColumn: "created",
        sortAscending: false,
        filters: [
          {
            column: "hq.id",
            value: hqId
          }
        ],
        columns: [
          "location.address",
          "id",
          "agent.email",
          "url",
          "nonCanonicalUrls"
        ]
      };

    case "Agent":
      return {
        serviceName: "read-layer",
        indexName: "agent",
        sortOnColumn: "emailAddress",
        sortAscending: true,
        filters: [
          {
            column: "offices.hq.id",
            value: hqId
          }
        ],
        columns: ["name", "id", "emailAddress"]
      };

    case "Office":
      return {
        serviceName: "read-layer",
        indexName: "office",
        sortOnColumn: "name",
        sortAscending: true,
        filters: [
          {
            column: "hq.id",
            value: hqId
          },
          {
            column: "active",
            value: "true"
          }
        ],
        columns: ["name", "id", "displayName"]
      };

    default:
      throw new Error("Unsupported subject type");
  }
};
